<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- 채팅 필터 영역 -->
    <div class="flex-shrink-0 border-b">
      <div class="text-sm font-medium text-gray-700 p-3">
        {{ bondName }} 호가 목록
      </div>
    </div>

    <!-- 새 호가 알림 -->
    <div v-if="showNewQuoteAlert"
      class="bg-blue-50 border-b border-blue-200 px-3 py-1.5 text-xs text-blue-700 cursor-pointer hover:bg-blue-100 transition-colors"
      @click="scrollToBottom">
      <div class="flex items-center justify-between">
        <span>새로운 호가가 등장했습니다</span>
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
      </div>
    </div>

    <!-- 채팅 목록 -->
    <div ref="chatListContainer" class="flex-1 overflow-y-auto" @scroll="handleScroll">
      <table class="min-w-full divide-y divide-gray-200 table-fixed">
        <colgroup>
          <col style="width: 70px" />
          <col />
          <col style="width: 150px" />
          <col style="width: 70px" />
        </colgroup>
        <thead class="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500">
              시간
            </th>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500">
              내용
            </th>
            <th class="px-3 py-2 text-xs font-medium text-gray-500">
              <div class="grid grid-cols-5 items-center">
                <span class="col-span-2 text-center">매도</span>
                <span class="col-span-1 text-center">가격</span>
                <span class="col-span-2 text-center">매수</span>
              </div>
            </th>
            <th class="px-2 py-2 text-right text-xs font-medium text-gray-500 whitespace-nowrap">
              스프레드
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="chat in filteredChatList" :key="chat.chatDateTime" class="hover:bg-gray-50">
            <td class="px-3 py-2 whitespace-nowrap text-xs text-gray-500 font-mono">
              <div class="flex items-center">
                <span>{{ formatTime(chat.chatDateTime) }}</span>
              </div>
            </td>
            <td class="px-3 py-2 text-xs overflow-hidden">
              <div class="flex items-center text-gray-700 overflow-hidden">
                <span v-if="chat.senderName" class="mr-2 text-xs font-medium emoji-display flex-shrink-0"
                  :title="chat.senderName">
                  {{ getUserEmoji(chat.senderName) }}
                </span>
                <span class="truncate block">
                  {{ chat.content }}
                </span>
              </div>
            </td>
            <td class="px-3 py-2 whitespace-nowrap text-xs">
              <div class="w-full">
                <!-- 매도/매수 가격 시각적 대비를 위한 UI 개선 -->
                <div class="grid grid-cols-5 items-center price-grid">
                  <!-- 매도 가격 (왼쪽 정렬) -->
                  <div v-if="chat.tradeType === 'SELL' && chat.quoteYield?.yield"
                    class="col-span-2 text-right pr-2 font-medium">
                    {{ formatChatYield(chat.quoteYield) }}
                  </div>
                  <div v-else class="col-span-2"></div>

                  <!-- 중앙 구분선 - 항상 고정 위치 -->
                  <div class="col-span-1 flex justify-center divider-container">
                    <div class="h-5 border-r border-gray-300"></div>
                  </div>

                  <!-- 매수 가격 (오른쪽 정렬) -->
                  <div v-if="chat.tradeType === 'BUY' && chat.quoteYield?.yield"
                    class="col-span-2 text-left pl-2 font-medium">
                    {{ formatChatYield(chat.quoteYield) }}
                  </div>
                  <div v-else class="col-span-2"></div>
                </div>
              </div>
            </td>
            <td class="px-2 py-2 whitespace-nowrap text-xs text-right">
              <span v-if="
                chat.quoteYield?.yield &&
                selectedBond &&
                selectedBond.evaluatedYield
              " :class="calculateSpread(
                selectedBond.evaluatedYield,
                chat.quoteYield
              ) < 0
                ? 'text-red-600'
                : 'text-blue-600'
                ">
                {{
                  formatSpread(
                    calculateSpread(
                      selectedBond.evaluatedYield,
                      chat.quoteYield
                    )
                  )
                }}
              </span>
            </td>
          </tr>
          <tr v-if="filteredChatList.length === 0">
            <td colspan="4" class="px-3 py-4 text-center text-gray-500 text-sm">
              채팅 내역이 없습니다
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, nextTick, onMounted } from 'vue';
import { format } from 'date-fns';

export default {
  name: 'ChatListComponent',
  emits: ['clear-new-quote-alert'],
  props: {
    chatList: {
      type: Array,
      required: true,
    },
    bondName: {
      type: String,
      default: '',
    },
    selectedBond: {
      type: Object,
      default: null,
    },
    isFirstLoad: {
      type: Boolean,
      default: false,
    },
    hasNewQuote: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // 채팅 필터링 옵션
    const hideFragmentTrades = ref(false);
    const hideWithoutPrice = ref(false);

    // 사용자 이름과 이모지 매핑을 저장할 객체
    const userEmojiMap = ref({});

    // 채팅 컨테이너 ref
    const chatListContainer = ref(null);

    // 실시간 업데이트 관련 상태
    const isAtBottom = ref(true);

    // 새 호가 알림 표시 여부 (외부 prop 기반)
    const showNewQuoteAlert = computed(() => {
      return props.hasNewQuote && !isAtBottom.value;
    });

    // 동물 이모지 배열
    const animalEmojis = [
      '🐶',
      '🐱',
      '🐭',
      '🐹',
      '🐰',
      '🦊',
      '🐻',
      '🐼',
      '🐨',
      '🦁',
      '🐯',
      '🦓',
      '🦒',
      '🐘',
      '🦏',
      '🦛',
      '🐄',
      '🐎',
      '🦄',
      '🦌',
      '🐕',
      '🐩',
      '🐈',
      '🦔',
      '🦇',
      '🐒',
      '🦍',
      '🦧',
      '🐿️',
      '🦦',
      '🦨',
      '🦥',
      '🦝',
      '🐢',
      '🐇',
      '🐓',
      '🐤',
      '🦢',
      '🦉',
      '🦩',
      '🦚',
      '🦜',
      '🐦',
      '🐧',
      '🐔',
      '🦆',
      '🦅',
      '🦖',
      '🦕',
      '🐊',
      '🐍',
      '🐲',
      '🐉',
      '🦎',
      '🐙',
      '🦑',
      '🦐',
      '🦞',
      '🦀',
      '🐡',
      '🐠',
      '🐟',
      '🐬',
      '🐳',
      '🐋',
      '🦈',
      '🐅',
      '🐆',
      '🦓',
      '🦍',
      '🦧',
      '🐘',
      '🦛',
      '🦏',
      '🐪',
      '🐫',
      '🦒',
      '🦘',
      '🐃',
      '🐂',
      '🐄',
      '🐎',
      '🐖',
      '🐏',
      '🐑',
      '🦙',
      '🐐',
      '🦌',
      '🐕',
      '🐩',
      '🦮',
      '🐈',
      '🐈‍⬛',
      '🪶',
      '🐓',
      '🦃',
      '🦤',
      '🦚',
      '🦜',
      '🦢',
      '🦩',
      '🕊️',
      '🦅',
      '🦆',
      '🦉',
      '🪹',
      '🪺',
      '🦇',
      '🐝',
      '🪱',
      '🐛',
      '🦋',
      '🐌',
      '🐞',
      '🐜',
      '🦂',
      '🦞',
      '🦐',
      '🦑',
      '🦀',
      '🐙',
    ];

    // 수익률 포맷팅 함수
    const formatYield = (yieldValue) => {
      if (yieldValue === null || yieldValue === undefined) return '-';
      return yieldValue.toFixed(3);
    };

    // 스프레드 계산 함수
    const calculateSpread = (evaluatedYield, yieldData) => {
      if (!evaluatedYield || !yieldData || yieldData.yield === null) return 0;

      let targetYield = yieldData.yield;
      if (yieldData.isSet && yieldData.setYield !== null) {
        // 세트일 때 평균값 사용
        targetYield = (yieldData.yield + yieldData.setYield) / 2;
      }

      return ((targetYield - evaluatedYield) * 100).toFixed(1);
    };

    // 스프레드 포맷팅 함수
    const formatSpread = (spread) => {
      if (spread === 0) return '-';
      const numSpread = parseFloat(spread);
      return numSpread > 0 ? `+${numSpread}bp` : `${numSpread}bp`;
    };

    // 시간 포맷팅
    const formatTime = datetime => {
      return format(new Date(datetime), 'HH:mm:ss');
    };

    // 필터링된 채팅 목록
    const filteredChatList = computed(() => {
      let filtered = props.chatList;

      // 자투리 제외 필터
      if (hideFragmentTrades.value) {
        filtered = filtered.filter(chat => !chat.isFractional);
      }

      // 가격 미포함 제외 필터
      if (hideWithoutPrice.value) {
        filtered = filtered.filter(chat => chat.quoteYield?.yield !== null);
      }

      return filtered;
    });

    const scrollToBottom = () => {
      nextTick(() => {
        if (chatListContainer.value) {
          chatListContainer.value.scrollTop =
            chatListContainer.value.scrollHeight;
          isAtBottom.value = true;
          // 스크롤이 최하단으로 이동하면 알림 제거
          if (props.hasNewQuote) {
            emit('clear-new-quote-alert');
          }
        }
      });
    };

    // 스크롤 위치 감지
    const handleScroll = () => {
      if (chatListContainer.value) {
        const { scrollTop, scrollHeight, clientHeight } = chatListContainer.value;
        const threshold = 50; // 50px 임계값
        const wasAtBottom = isAtBottom.value;
        isAtBottom.value = scrollHeight - scrollTop - clientHeight < threshold;

        // 스크롤이 최하단으로 이동하면 알림 제거
        if (!wasAtBottom && isAtBottom.value && props.hasNewQuote) {
          emit('clear-new-quote-alert');
        }
      }
    };

    // 최초 로드 시 스크롤 최하단 이동
    onMounted(() => {
      scrollToBottom(); // 항상 최하단으로
    });

    // 채팅 목록 변경 감지 (실시간 업데이트)
    watch(
      () => props.chatList,
      (newChatList, oldChatList) => {
        if (newChatList.length > (oldChatList?.length || 0)) {
          // 새로운 채팅이 추가된 경우
          nextTick(() => {
            if (isAtBottom.value) {
              scrollToBottom(); // 최하단에 있으면 자동 스크롤
            }
          });
        }
      },
      { deep: true }
    );

    // 사용자 이름에 해당하는 이모지 가져오기
    const getUserEmoji = userName => {
      // 이미 매핑된 이모지가 있으면 반환
      if (userEmojiMap.value[userName]) {
        return userEmojiMap.value[userName];
      }

      // 새로운 이모지 할당 - 해시 기반으로 일관성 있게 할당
      const hash = userName.split('').reduce((acc, char) => {
        return acc + char.charCodeAt(0);
      }, 0);
      const randomIndex = hash % animalEmojis.length;
      userEmojiMap.value[userName] = animalEmojis[randomIndex];
      return userEmojiMap.value[userName];
    };

    // 채팅 금리 포매팅 (소수부분만 표시)
    const formatChatYield = (yieldData) => {
      if (!yieldData || yieldData.yield === null || yieldData.yield === undefined) return '-';

      const getDecimalPart = (value) => {
        const str = value.toFixed(3);
        const decimalPart = str.split('.')[1];
        return decimalPart || '000';
      };

      if (yieldData.isSet && yieldData.setYield !== null) {
        // 세트일 때 소수부분만 "55 555" 형식
        const yieldDecimal = getDecimalPart(yieldData.yield);
        const setYieldDecimal = getDecimalPart(yieldData.setYield);
        return `${yieldDecimal} ${setYieldDecimal}`;
      }

      // 세트가 아니면 소수부분만
      return getDecimalPart(yieldData.yield);
    };

    return {
      hideFragmentTrades,
      hideWithoutPrice,
      filteredChatList,
      formatTime,
      formatYield,
      getUserEmoji,
      calculateSpread,
      formatSpread,
      chatListContainer,
      formatChatYield,
      showNewQuoteAlert,
      scrollToBottom,
      handleScroll,
    };
  },
};
</script>

<style scoped>
.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.buy-indicator {
  position: relative;
}

.sell-indicator {
  position: relative;
}

/* 이모지 표시 문제 해결을 위한 스타일 */
[class*='emoji'],
.emoji-display {
  font-family:
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
  font-size: 16px;
  line-height: 1;
  vertical-align: middle;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

/* 매수/매도 가격 UI 개선 */
.price-grid {
  min-height: 1.5rem;
  width: 100%;
}

.divider-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 내용이 길어질 때 처리 */
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: block;
}
</style>
